import { Form, Spin, Space } from 'antd'
import { Main } from 'components/ActionStyles/ActionStyles'
import Button from 'components/Button'
import Drawer from 'components/Drawer'
import Flex from 'components/Flex'
import ErrorMessage from 'components/UIComponents/MessagePopup/ErrorMessage'
import FileReader from 'components/Uploader/FileReader'
import CrossFillIcon from 'components/icons/CrossFillIcon'
import DownLoad from 'components/icons/DownLoad'
import UploadIcon from 'components/icons/UploadIcon'
import DeleteBin from 'components/icons/DeleteIcon'
import {
  CandidateInfo,
  FileInfoDiv,
  FileNameDiv,
  FooterFlex,
  ResultDiv,
  ResultIconDiv,
  StyledUpload,
  TemolateDiv,
  UploadFileDiv,
  UploadFlex,
  UploadIconDiv,
  UploadMsgFlex,
  Wrapper,
} from 'modules/Exp-Candidates/DownloadAndUpload/UploadResultDrawer/style'
import { uploadFile } from 'modules/ManageProfile/actions'
import {
  bulkUploadInviteCandidates,
  bulkUploadAppliedCandidates,
  getInviteCandidatesCount,
  getInviteCandidates,
} from 'modules/Exp-Candidates/actions'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import SuccessMessage from 'utils/SuccessMessage'
import UploadResultIcon from 'components/icons/UploadResultIcon'
import * as XLSX from 'xlsx'
import {
  InfoSection,
  SmallButtons,
  SampleDocContainer,
  BoldText,
  LightText,
} from '../style'

console.log('[AddCandidates v3] UploadInviteDrawer module loaded')

const UploadInviteDrawer = ({
  visible,
  setVisible,
  roleId,
  setIsDrawer,
  setInviteTableRefresh,
  setInvitedCount,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()

  const [fileURL, setFileURL] = useState('')
  const [file, setFile] = useState(null)
  const [isFileLoader, setIsFileLoader] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [uploadSummary, setUploadSummary] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  // After a file is staged, ask "Do you wish to mark the candidates as applied?".
  // null = unanswered, true = applied path (Google Sheet only), false = invite path (existing).
  const [markAsApplied, setMarkAsApplied] = useState(null)
  // Asked only after markAsApplied === true. null = unanswered, true = current flow,
  // false = backend prepends `##NO##` to the Google Sheet title.
  const [requiresNormalization, setRequiresNormalization] = useState(null)

  const onChangeFileUpload = async info => {
    console.log('[AddCandidates v3] onChangeFileUpload fired. status =', info?.file?.status, 'name =', info?.file?.name)
    if (info?.file?.status !== 'done') return

    setIsFileLoader(true)
    setFile(info?.file)
    setFileURL('')
    setErrorMessage('')

    try {
      // Sanitize filename: original names contain spaces and `#` (role-id pattern)
      // which break https.get URL parsing on the backend. Hardcode the name and
      // preserve only the extension so the xlsx/csv dispatcher still works.
      const ext = (info?.file?.name?.split('.').pop() || 'xlsx').toLowerCase()
      const safeName = `FileUploaded.${ext}`
      console.log('[AddCandidates v3] originalName:', info?.file?.name, '| safeName:', safeName)
      const response = await dispatch(
        uploadFile(info?.file?.originFileObj, safeName)
      )
      console.log('[AddCandidates v3] uploadFile response:', response)
      if (response) {
        setFileURL(response)
      } else {
        setErrorMessage('Failed to upload file. Please try again.')
      }
    } catch (err) {
      setErrorMessage(
        err?.message || 'Could not upload the file. Please try again.'
      )
    } finally {
      setIsFileLoader(false)
    }
  }

  const handleDownloadSample = () => {
    const wb = XLSX.utils.book_new()
    const wsData = [['First Name', 'Last Name', 'Email', 'Phone']]
    const ws = XLSX.utils.aoa_to_sheet(wsData)
    ws['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 15 }]
    XLSX.utils.book_append_sheet(wb, ws, 'SampleTemplate')
    XLSX.writeFile(wb, 'SampleTemplate.xlsx')
  }

  const handleRemoveFile = () => {
    setFile(null)
    setFileURL('')
    setErrorMessage('')
    setMarkAsApplied(null)
    setRequiresNormalization(null)
  }

  const handleMarkAsAppliedChange = value => {
    setMarkAsApplied(value)
    if (value !== true) setRequiresNormalization(null)
  }

  const onFinish = async () => {
    if (!fileURL) {
      ErrorMessage('Please upload the file!')
      return
    }
    if (markAsApplied === null) {
      ErrorMessage('Please choose whether to mark the candidates as applied.')
      return
    }
    if (markAsApplied === true && requiresNormalization === null) {
      ErrorMessage('Please choose whether this file requires normalization.')
      return
    }

    setIsSubmitting(true)
    try {
      const payload = { fileUrl: fileURL }

      if (markAsApplied) {
        // Yes path → versioned Google Sheet, no email/WA, no DB candidate marking.
        payload.requiresNormalization = requiresNormalization
        const response = await dispatch(
          bulkUploadAppliedCandidates(roleId, payload)
        )
        if (response?.status === 200) {
          setUploadSuccess(true)
          setUploadSummary({ ...response?.data?.data, _markedAsApplied: true })
          SuccessMessage('Sheet created successfully!')
        } else {
          const errMsg =
            response?.data?.message ||
            response?.response?.data?.message ||
            'Failed to create applied-candidates sheet'
          ErrorMessage(errMsg)
        }
      } else {
        // No path → existing invite flow (emails + WhatsApp + DB insert).
        const response = await dispatch(
          bulkUploadInviteCandidates(roleId, payload)
        )
        if (response?.status === 200) {
          setUploadSuccess(true)
          setUploadSummary(response?.data?.data)
          SuccessMessage('Invite candidates uploaded successfully!')
          handleClose()
          dispatch(getInviteCandidates(roleId, { page: 0, limit: 20 }))
          dispatch(getInviteCandidatesCount(roleId)).then(res => {
            if (res?.status === 200) {
              setInvitedCount(res?.data?.data?.count || 0)
              setInviteTableRefresh(prev => prev + 1)
            }
          })
        } else {
          const errMsg =
            response?.data?.message ||
            response?.response?.data?.message ||
            'Failed to upload invite candidates'
          ErrorMessage(errMsg)
        }
      }
    } catch (err) {
      ErrorMessage(err?.message || 'Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setVisible(false)
    setFile(null)
    setFileURL('')
    setUploadSuccess(false)
    setUploadSummary(null)
    setErrorMessage('')
    setMarkAsApplied(null)
    setRequiresNormalization(null)
    form.resetFields()
  }

  const successContent = () => {
    if (uploadSummary?._markedAsApplied) {
      return (
        <Wrapper>
          <UploadMsgFlex column center>
            <ResultIconDiv>
              <UploadResultIcon />
            </ResultIconDiv>
            <ResultDiv>Sheet Created!</ResultDiv>
            <FileInfoDiv>
              {uploadSummary?.sheetTitle}
            </FileInfoDiv>
            <FileInfoDiv>
              {uploadSummary?.rowCount || 0} rows added (Version{' '}
              {uploadSummary?.version})
            </FileInfoDiv>
            {uploadSummary?.sheetUrl && (
              <FileInfoDiv>
                <a
                  href={uploadSummary.sheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff' }}
                >
                  Open Google Sheet
                </a>
              </FileInfoDiv>
            )}
          </UploadMsgFlex>
        </Wrapper>
      )
    }
    return (
      <Wrapper>
        <UploadMsgFlex column center>
          <ResultIconDiv>
            <UploadResultIcon />
          </ResultIconDiv>
          <ResultDiv>Upload Successful!</ResultDiv>
          <FileInfoDiv>
            {uploadSummary?.newInvitesSent || 0} invites sent
          </FileInfoDiv>
          {uploadSummary?.duplicatesSkipped > 0 && (
            <FileInfoDiv>
              {uploadSummary.duplicatesSkipped} duplicates skipped
            </FileInfoDiv>
          )}
          {uploadSummary?.emailsFailed > 0 && (
            <FileInfoDiv style={{ color: '#e53935' }}>
              {uploadSummary.emailsFailed} emails failed
            </FileInfoDiv>
          )}
        </UploadMsgFlex>
      </Wrapper>
    )
  }

  const formContent = () => (
    <Wrapper>
      <Form form={form} onFinish={onFinish} layout="vertical">
        {/* Download Sample */}
        <InfoSection>
          <Space>
            <SmallButtons
              height={'30px'}
              width="30px"
              border={true}
              icon={<DownLoad />}
            />
            <SampleDocContainer>
              <BoldText>Download Sample Template</BoldText>
              <LightText>
                Download the sample Excel template, fill in candidate details, and
                upload it below.
              </LightText>
            </SampleDocContainer>
          </Space>
          <Main>
            <Button.Secondary
              onClick={handleDownloadSample}
              trigger={['click']}
              text="Download"
            ></Button.Secondary>
          </Main>
        </InfoSection>
        {/* <CandidateInfo>Download Sample Template</CandidateInfo>
        <TemolateDiv>
          Download the sample Excel template, fill in candidate details, and
          upload it below.
        </TemolateDiv>
        <Flex
          style={{ marginTop: '15px', cursor: 'pointer' }}
          onClick={handleDownloadSample}
        >
          <DownLoadIcon />
          <span
            style={{
              marginLeft: '8px',
              color: '#1890ff',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            Download Sample Excel
          </span>
        </Flex> */}

        {/* File Upload */}
        <div style={{ marginTop: '25px' }}>
          <CandidateInfo>Upload Excel (or) CSV File</CandidateInfo>
          {!file ? (
            <StyledUpload
              accept=".xlsx,.csv"
              showUploadList={false}
              customRequest={({ onSuccess }) => {
                setTimeout(() => onSuccess('ok'), 0)
              }}
              onChange={onChangeFileUpload}
            >
              <UploadFlex column center>
                {isFileLoader ? (
                  <Spin />
                ) : (
                  <>
                    <Flex center style={{ paddingBottom: '10px' }}>
                      <SmallButtons icon={<UploadIcon width="48" height="48" />} />
                    </Flex>
                    <UploadFileDiv>
                      Click or drag file to upload
                    </UploadFileDiv>
                    <FileNameDiv>Format .xlsx or .csv</FileNameDiv>
                  </>
                )}
              </UploadFlex>
            </StyledUpload>
          ) : (
            <Flex
              style={{
                marginTop: '15px',
                padding: '12px 16px',
                background: '#f5f7fa',
                borderRadius: '8px',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: '14px', color: '#333' }}>
                {file?.name}
              </span>
              <span
                style={{ cursor: 'pointer' }}
                onClick={handleRemoveFile}
              >
                <DeleteBin />
              </span>
            </Flex>
          )}
          {errorMessage && (
            <div style={{ color: '#e53935', fontSize: '13px', marginTop: '8px' }}>
              {errorMessage}
            </div>
          )}
        </div>

        {/* Yes/No question — only shown after file is staged */}
        {fileURL && !isFileLoader && (
          <div
            style={{
              marginTop: '24px',
              padding: '16px 18px',
              border: '1px solid #e0e4ea',
              borderRadius: '8px',
              background: '#fafbfc',
            }}
          >
            <CandidateInfo style={{ marginBottom: '12px' }}>
              Do you wish to mark the candidates as applied?
            </CandidateInfo>
            <Space>
              <Button.Secondary
                text="Yes"
                onClick={() => handleMarkAsAppliedChange(true)}
                style={
                  markAsApplied === true
                    ? { backgroundColor: '#3249D7', borderColor: '#3249D7', color: '#ffffff', fontWeight: 600 }
                    : undefined
                }
              />
              <Button.Secondary
                text="No"
                onClick={() => handleMarkAsAppliedChange(false)}
                style={
                  markAsApplied === false
                    ? { backgroundColor: '#3249D7', borderColor: '#3249D7', color: '#ffffff', fontWeight: 600 }
                    : undefined
                }
              />
            </Space>
            <div
              style={{
                marginTop: '10px',
                fontSize: '12px',
                color: '#666',
                lineHeight: '1.5',
              }}
            >
              {markAsApplied === true &&
                'A new versioned Google Sheet will be created with the uploaded data. No emails will be sent.'}
              {markAsApplied === false &&
                'Invite emails and WhatsApp messages will be sent to the candidates.'}
              {markAsApplied === null &&
                'Choose Yes to record the candidates as already applied (sheet only). Choose No to send invitation emails.'}
            </div>

            {markAsApplied === true && (
              <div
                style={{
                  marginTop: '18px',
                  paddingTop: '16px',
                  borderTop: '1px dashed #d9dde3',
                }}
              >
                <CandidateInfo style={{ marginBottom: '12px' }}>
                  Did this file require normalization?
                </CandidateInfo>
                <Space>
                  <Button.Secondary
                    text="Yes"
                    onClick={() => setRequiresNormalization(true)}
                    style={
                      requiresNormalization === true
                        ? { backgroundColor: '#3249D7', borderColor: '#3249D7', color: '#ffffff', fontWeight: 600 }
                        : undefined
                    }
                  />
                  <Button.Secondary
                    text="No"
                    onClick={() => setRequiresNormalization(false)}
                    style={
                      requiresNormalization === false
                        ? { backgroundColor: '#3249D7', borderColor: '#3249D7', color: '#ffffff', fontWeight: 600 }
                        : undefined
                    }
                  />
                </Space>
                <div
                  style={{
                    marginTop: '10px',
                    fontSize: '12px',
                    color: '#666',
                    lineHeight: '1.5',
                  }}
                >
                  {requiresNormalization === true &&
                    'Standard flow — sheet title uses the usual format.'}
                  {requiresNormalization === false &&
                    'Sheet title will be prefixed with ##NO## to flag that this upload skipped normalization.'}
                  {requiresNormalization === null &&
                    'Yes keeps the current flow. No prefixes the Google Sheet title with ##NO##.'}
                </div>
              </div>
            )}
          </div>
        )}
      </Form>
    </Wrapper>
  )
  const footer = () => {
    if (uploadSuccess) {
      return null
    }
    return (
      <FooterFlex right>
        <Button.Secondary
          text="Cancel"
          onClick={handleClose}
        />
        <Button.Primary
          text={isSubmitting ? 'Uploading...' : 'Submit'}
          onClick={() => form.submit()}
          disabled={
            isSubmitting ||
            !fileURL ||
            markAsApplied === null ||
            (markAsApplied === true && requiresNormalization === null)
          }
        />
      </FooterFlex>
    )
  }
  return (
    <Drawer
      title="Add Candidates"
      placement="right"
      visible={visible}
      content={uploadSuccess ? successContent() : formContent()}
      onClose={handleClose}
      footer={footer()}
      closable
      width="40%"
    />
  )
}

export default UploadInviteDrawer
